import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CompositionEvent,
  type KeyboardEvent,
} from 'react';

/** Windows/IME が変換中の keydown に付ける legacy keyCode */
export const IME_KEY_CODE = 229;

/** compositionend 直後の Enter を無視する時間（意図的な連続 Enter 送信は潰さない） */
export const COMPOSITION_END_ENTER_GUARD_MS = 50;

export type ImeSubmitKeyLike = {
  isComposing?: boolean;
  keyCode?: number;
};

/**
 * IME 変換中・確定直後の Enter を送信に使ってはいけないか。
 * React の isComposing state だけでは compositionend 直後の keydown を取りこぼす。
 */
export function isImeSubmitBlockedKey(
  e: ImeSubmitKeyLike,
  flags?: { composing?: boolean; justEnded?: boolean }
): boolean {
  return (
    flags?.composing === true ||
    flags?.justEnded === true ||
    e.isComposing === true ||
    e.keyCode === IME_KEY_CODE
  );
}

export function useImeCompositionGuard(options?: {
  onCompositionEnd?: (e: CompositionEvent<HTMLElement>) => void;
}) {
  const [isComposing, setIsComposing] = useState(false);
  const composingRef = useRef(false);
  const justEndedRef = useRef(false);
  const endTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCompositionEndExtraRef = useRef(options?.onCompositionEnd);
  onCompositionEndExtraRef.current = options?.onCompositionEnd;

  const clearEndTimer = useCallback(() => {
    if (endTimerRef.current !== null) {
      clearTimeout(endTimerRef.current);
      endTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearEndTimer(), [clearEndTimer]);

  const onCompositionStart = useCallback(
    (_e: CompositionEvent<HTMLElement>) => {
      clearEndTimer();
      composingRef.current = true;
      justEndedRef.current = false;
      setIsComposing(true);
    },
    [clearEndTimer]
  );

  const onCompositionEnd = useCallback(
    (e: CompositionEvent<HTMLElement>) => {
      composingRef.current = false;
      justEndedRef.current = true;
      setIsComposing(false);
      onCompositionEndExtraRef.current?.(e);
      clearEndTimer();
      endTimerRef.current = setTimeout(() => {
        justEndedRef.current = false;
        endTimerRef.current = null;
      }, COMPOSITION_END_ENTER_GUARD_MS);
    },
    [clearEndTimer]
  );

  const shouldBlockEnterSubmit = useCallback((e: KeyboardEvent) => {
    const native = e.nativeEvent;
    return isImeSubmitBlockedKey(
      { isComposing: native.isComposing, keyCode: native.keyCode },
      { composing: composingRef.current, justEnded: justEndedRef.current }
    );
  }, []);

  return {
    isComposing,
    onCompositionStart,
    onCompositionEnd,
    shouldBlockEnterSubmit,
  };
}
